import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  Spinner,
  Flex,
  VStack,
  HStack,
  useDisclosure,
  Heading
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBBPSCategories,
  updateBBPSCategory,
  deleteBBPSCategory,
  clearBBPSCategories
} from '@store/actions/bbpsActions';
import { setAlert } from '@store/actions/alertActions';
import { MdEdit, MdDelete } from 'react-icons/md';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import ConfirmModal from '@components/Modal/ConfirmModal';
import GenericCard from '@components/GenericCard';
import { CARD_TYPE, ROLES, ALERT_STATUS_TYPE } from '@constants';
import useAuthRole from '@hooks/useAuthRole';
import { BBPS_CATEGORY_FORM_CONFIG } from './bbpsCategoryFormConfig';
import { getStatusColor } from './utils';

export const BBPSCategories = () => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmDeleteOpen,
    onOpen: onConfirmDeleteOpen,
    onClose: onConfirmDeleteClose
  } = useDisclosure();

  const { data: categories, isLoading, isError, error, isUpdating } = useSelector(
    (state) => state.bbps.categories
  );


  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Check permissions
  const canManageCategories = hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.BBPS_MANAGER_ROLE);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(getBBPSCategories());
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => dispatch(clearBBPSCategories());
  }, [dispatch]);

  const handleFormSubmit = (formData) => {
    if (!editingCategory) return;

    // Update existing category
    dispatch(updateBBPSCategory({
      updateData: {
        ...formData,
        categoryId: editingCategory.categoryId
      },
      onSuccess: () => {
        setEditingCategory(null);
        onClose();
        dispatch(setAlert({
          title: 'Category updated successfully!',
          status: ALERT_STATUS_TYPE.SUCCESS
        }));
      },
      onError: (error) => {
        console.error('Failed to update category:', error);
      }
    }));
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    onOpen();
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    onConfirmDeleteOpen();
  };

  const handleDeleteConfirm = () => {
    if (!categoryToDelete) return;

    setDeletingCategoryId(categoryToDelete.categoryId);

    dispatch(deleteBBPSCategory({
      categoryId: categoryToDelete.categoryId,
      onSuccess: () => {
        setDeletingCategoryId(null);
        setCategoryToDelete(null);
        onConfirmDeleteClose();
        dispatch(setAlert({
          title: 'Category deleted successfully!',
          status: ALERT_STATUS_TYPE.SUCCESS
        }));
      },
      onError: () => {
        setDeletingCategoryId(null);
        setCategoryToDelete(null);
        onConfirmDeleteClose();
      },
      onComplete: () => {
        setDeletingCategoryId(null);
        setCategoryToDelete(null);
        onConfirmDeleteClose();
      }
    }));
  };

  const handleConfirmDeleteClose = () => {
    setCategoryToDelete(null);
    onConfirmDeleteClose();
  };

  const getFormConfig = () => {
    // Pre-populate form with existing data for editing
    return BBPS_CATEGORY_FORM_CONFIG.map(field => ({
      ...field,
      value: editingCategory[field.name] || ''
    }));
  };

  return (
    <VStack alignItems="stretch" gap={6} width="100%">
      {/* Header */}
      <HStack justifyContent="space-between" alignItems="center">
        <Heading color="whiteAlpha.700" fontSize="2xl" fontWeight="500">
          BBPS Category Management
        </Heading>
      </HStack>

      {/* Content */}
      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" py={10}>
          <Spinner thickness="4px" size="lg" color="colorPrimary" />
        </Flex>
      ) : isError ? (
        <Box color="red.500" p={4} borderRadius="md" borderWidth="1px">
          <Text>Error fetching categories: {error?.message || 'Unknown error'}</Text>
        </Box>
      ) : categories?.length > 0 ? (
        <VStack alignItems="stretch" gap={2}>
          {categories.map((category, index) => (
            <GenericCard
              key={category.categoryId || index}
              cardData={category}
              cardType={CARD_TYPE.BBPS_CATEGORY_CARD}
              status={{
                text: category.enabled ? 'Enabled' : 'Disabled',
                color: getStatusColor(category.enabled)
              }}
              ActionButtons={
                canManageCategories
                  ? [
                    <Button key="edit" size="sm" colorScheme="blue" leftIcon={<MdEdit />} onClick={() => handleEditCategory(category)}>
                      Edit
                    </Button>,
                    <Button key="delete" size="sm" colorScheme="red" leftIcon={<MdDelete />} onClick={() => handleDeleteClick(category)} isLoading={deletingCategoryId === category.categoryId}>
                      Delete
                    </Button>
                  ]
                  : []
              }
            />
          ))}
        </VStack>
      ) : (
        <Box textAlign="center" py={10}>
          <Text color="whiteAlpha.600">No categories found</Text>
        </Box>
      )}

      {/* Edit Category Modal */}
      {canManageCategories && editingCategory && (
        <ModalLayout isOpen={isOpen} onClose={onClose}>
          <DynamicInputModal
            title="Edit Category"
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={handleFormSubmit}
            fields={getFormConfig()}
            isLoading={isUpdating}
          />
        </ModalLayout>
      )}

      {/* Delete Confirmation Modal */}
      <ModalLayout isOpen={isConfirmDeleteOpen} onClose={handleConfirmDeleteClose}>
        <ConfirmModal
          prompt={`Are you sure you want to delete the category "${categoryToDelete?.category}"?`}
          handleSubmit={handleDeleteConfirm}
          isLoading={deletingCategoryId === categoryToDelete?.categoryId}
        />
      </ModalLayout>
    </VStack>
  );
};
